import { apiHelpers } from './api';

// Poll types
export type PollStatus = 'ACTIVE' | 'CLOSED' | 'DRAFT';

export interface Poll {
  id: string;
  title: string;
  description?: string;
  createdBy: string;
  allowMultiple: boolean;
  isAnonymous: boolean;
  expiresAt?: string;
  status: PollStatus;
  createdAt: string;
  updatedAt: string;
  options: PollOption[];
  creator?: {
    username: string;
    displayName?: string;
    avatarUrl?: string;
  };
  totalVotes: number;
  hasVoted: boolean;
  userVotes: string[]; // Option IDs the current user voted for
}

export interface PollOption {
  id: string;
  pollId: string;
  text: string;
  order: number;
  createdAt: string;
  voteCount: number;
  percentage: number;
  voters?: {
    userId: string;
    username: string;
    displayName?: string;
    avatarUrl?: string;
  }[];
}

export interface CreatePollInput {
  title: string;
  description?: string;
  allowMultiple?: boolean;
  isAnonymous?: boolean;
  expiresAt?: string;
  status?: PollStatus;
  options: CreatePollOptionInput[];
}

export interface CreatePollOptionInput {
  text: string;
  order?: number;
}

export interface UpdatePollInput {
  title?: string;
  description?: string;
  status?: PollStatus;
  expiresAt?: string;
}

export interface VotePollInput {
  optionIds: string[];
}

export interface PollStats {
  totalPolls: number;
  activePolls: number;
  totalVotes: number;
  pollsCreated: number;
}

/**
 * Create a new poll
 */
export const createPoll = async (data: CreatePollInput): Promise<Poll> => {
  return apiHelpers.post<Poll>('/polls', data);
};

/**
 * Get all polls with optional filters
 */
export const getPolls = async (
  status?: PollStatus,
  limit: number = 50,
  offset: number = 0
): Promise<Poll[]> => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());

  return apiHelpers.get<Poll[]>(`/polls?${params.toString()}`);
};

/**
 * Get a specific poll by ID
 */
export const getPollById = async (pollId: string): Promise<Poll> => {
  return apiHelpers.get<Poll>(`/polls/${pollId}`);
};

/**
 * Update a poll (creator only)
 */
export const updatePoll = async (
  pollId: string,
  data: UpdatePollInput
): Promise<Poll> => {
  return apiHelpers.put<Poll>(`/polls/${pollId}`, data);
};

/**
 * Delete a poll (creator only)
 */
export const deletePoll = async (pollId: string): Promise<void> => {
  await apiHelpers.delete(`/polls/${pollId}`);
};

/**
 * Vote on a poll
 */
export const votePoll = async (
  pollId: string,
  data: VotePollInput
): Promise<Poll> => {
  return apiHelpers.post<Poll>(`/polls/${pollId}/vote`, data);
};

/**
 * Remove vote from a poll
 */
export const removeVote = async (pollId: string): Promise<Poll> => {
  return apiHelpers.delete<Poll>(`/polls/${pollId}/vote`);
};

/**
 * Get poll statistics
 */
export const getPollStats = async (): Promise<PollStats> => {
  return apiHelpers.get<PollStats>('/polls/stats');
};

/**
 * Close a poll (shortcut for updating status to CLOSED)
 */
export const closePoll = async (pollId: string): Promise<Poll> => {
  return updatePoll(pollId, { status: 'CLOSED' });
};

/**
 * Reopen a poll (shortcut for updating status to ACTIVE)
 */
export const reopenPoll = async (pollId: string): Promise<Poll> => {
  return updatePoll(pollId, { status: 'ACTIVE' });
};
